import React, { useState } from "react"
import { Box, Button, List, makeStyles, TextField, Typography } from "@material-ui/core"
import { Add as AddIcon } from "@material-ui/icons"
import arrayMove from "array-move"
import { SortableContainer, SortableElement } from "react-sortable-hoc"
import Config from "react-global-configuration"
import userflow from "userflow.js"
import {
  UsersGroupsAssigner,
  ProcessScheduleAssigner,
  DraftBlockquote,
  MissingLocationAccessBlockquote,
  HasActiveJobsChangedBlockquote,
  HasActiveJobsScheduleChangedBlockquote,
} from ".."
import {
  JOB_PINNING,
  PROCESS_REPEAT,
  useLazyQueryActiveProcessJobs,
  useMutationCreateProcess,
  useMutationUpdateProcess,
} from "../../data"
import { ProcessStepCreator } from "./ProcessStepCreator"
import {
  mapToIds,
  toId,
  removeTypenames,
  useDateUtils,
  useFormUtils,
  useMountEffect,
  useDraft,
  hasIterator,
} from "../../utils"
import { useAuth } from "../../services"
import { LocationsAssigner, usersGroupsEveryoneItem } from "../Assigners"
import { CreatorActions } from "../Creators"
import { ColumnBox } from "../Boxes"
import Checkbox from "../Checkbox"
import { TextDivider } from "../TextDivider"

const initialState = {
  name: "",
  steps: [],
  after: null,
  due: null,
  pinning: {
    type: JOB_PINNING.NONE,
    period: null,
  },
  inactive: false,
  repeat: null,
  notify: {
    users: [],
    groups: [],
    type: 0,
    when: {
      hour: null,
      completed: null,
    },
  },
  locations: [],
  users: [],
  groups: [],
}

const initialStepState = {
  name: "",
  responseType: 0,
  description: null,
  expanded: true,
  uploads: [],
}

const useStyles = makeStyles(() => ({
  sortableListHelper: {
    zIndex: 99999,
  },
}))

const SortableProcessStepCreator = SortableElement((props) => <ProcessStepCreator {...props} />)

const SortableListContainer = SortableContainer(
  ({ items, onUpdateStep, onRemoveStep, onUploadingStateChange, disabled }) => (
    <List component="div">
      {items.map((item, index) => (
        <SortableProcessStepCreator
          key={index}
          index={index}
          stepIndex={index}
          step={item}
          onUpdate={onUpdateStep}
          onRemove={onRemoveStep}
          onUploadingStateChange={onUploadingStateChange}
          disabled={disabled}
          stepDisabled={disabled}
        />
      ))}
    </List>
  )
)

const ProcessCreator = ({ onClose, edit = null }) => {
  const classes = useStyles()
  const [createProcess, { loading: createLoading }] = useMutationCreateProcess()
  const [updateProcess, { loading: updateLoading }] = useMutationUpdateProcess()
  const [loadActiveProcessJobs, activeProcessJobsResult] = useLazyQueryActiveProcessJobs()
  const { isValid, isError, getErrorMessage, debounceTouched } = useFormUtils()
  const { scheduleDateToMoment } = useDateUtils()
  const {
    support: {
      userflow: { enabled: userflowEnabled },
    },
  } = Config.get()
  const {
    settings: { locations: userLocations },
  } = useAuth()
  const [isUploading, setIsUploading] = useState(false)

  const [showAssigner] = useState(false)
  const [nameTouched, setNameTouched] = useState(false)
  const [nameValid, setNameValid] = useState(false)
  const [stepNameValid, setStepNameValid] = useState(true)
  const [oneOffScheduleChanged, setOneOffScheduleChanged] = useState(false)

  const [name, setName] = useState(initialState.name)
  const [steps, setSteps] = useState(initialState.steps)
  const [after, setAfter] = useState(initialState.after)
  const [due, setDue] = useState(initialState.due)
  const [pinning, setPinning] = useState(initialState.pinning)

  const [inactive, setInactive] = useState(initialState.inactive)
  const [repeat, setRepeat] = useState(initialState.repeat)
  const [notify] = useState(initialState.notify)
  const [hasEveryone, setHasEveryone] = useState(false)
  const [locations, setLocations] = useState(null)
  const [users, setUsers] = useState(initialState.users)
  const [groups, setGroups] = useState(initialState.groups)

  const [draft, setDraft, removeDraft, draftUntouched] = useDraft("process_creator", null)

  const [initialPicked, setInitialPicked] = useState(null)

  useMountEffect(() => {
    if (edit) {
      loadActiveProcessJobs({ variables: { process: edit.id } })

      const noUsersOrGroups = !edit.users.length && !edit.groups.length

      // copy won't have any locations
      const isCopy = !edit.locations || !edit.locations.length

      setName(edit.name)
      setSteps([...edit.steps.map((step) => ({ expanded: false, ...step }))])
      setAfter(isCopy ? null : edit.after)
      setDue(isCopy ? null : edit.due)
      setPinning(edit.pinning)
      setInactive(edit.inactive)
      setRepeat(isCopy ? null : edit.repeat)
      setHasEveryone(noUsersOrGroups)
      setLocations([...mapLocations(edit.locations)])
      setUsers(mapToIds(edit.users))
      setGroups(mapToIds(edit.groups))
      setInitialPicked(isCopy ? [] : noUsersOrGroups ? [usersGroupsEveryoneItem] : [...edit.users, ...edit.groups])

      setNameValid(isValid(edit.name))
    } else {
      setLocations(userLocations.length > 1 ? [] : [userLocations[0]])
      setInitialPicked([])

      if (draft) {
        if (draft.name) {
          setNameTouched(true)
          setNameValid(true)
          setName(draft.name)
        }
        draft.steps && setSteps([...draft.steps])
        draft.after && setAfter(draft.after)
        draft.due && setDue(draft.due)
        draft.pinning && setPinning(draft.pinning)
        draft.repeat && setRepeat(draft.repeat)
        draft.inactive && setInactive(draft.inactive)
      }
    }
  })

  const mapLocations = (items) => [
    ...items.map((location) => {
      const item = userLocations.find((ul) => toId(ul) === toId(location))
      if (item) return item
      return { id: toId(location), name: "(Hidden)", noAccess: true }
    }),
  ]

  const handleClose = (event, isCancel) => {
    onClose(isCancel)
  }

  const setDraftProperty = (prop, value) => {
    if (edit) return
    setDraft((prev) => ({
      ...prev,
      [prop]: value,
    }))
  }

  const handleDiscardDraft = () => {
    removeDraft()
    handleResetState()
  }

  const handleResetState = () => {
    setNameTouched(false)
    setName(initialState.name)
    setSteps(initialState.steps)
    setAfter(initialState.after)
    setDue(initialState.due)
    setPinning(initialState.pinning)
    setInactive(initialState.inactive)
    setRepeat(initialState.repeat)
    setHasEveryone(false)
    setInitialPicked([])
  }

  const handleNameChange = (e) => {
    setName(e.target.value)
    setNameValid(isValid(e.target.value))
    setDraftProperty("name", e.target.value)
  }

  const handleSubmit = async (e, toggleActive = null) => {
    e.preventDefault()
    if (formValid()) {
      const cleanSteps = steps
        .map(({ id, name: stepName, responseType, description, uploads }) => ({
          id,
          name: stepName,
          responseType: responseType - 0,
          description,
          uploads: uploads.map((upload) => upload.id),
        }))
        .filter((t) => t.name)
      const cleanRepeat = {
        type: repeat.type,
        frequency: repeat.frequency,
        weekdays: removeTypenames(repeat.weekdays),
        months: removeTypenames(repeat.months),
      }
      const variables = {
        locations: locations.map((location) => toId(location)),
        name,
        steps: cleanSteps,
        after: removeTypenames(after),
        due: removeTypenames(due),
        inactive,
        pinning: removeTypenames(pinning),
        repeat: cleanRepeat,
        notify,
        users,
        groups,
      }
      if (toggleActive) variables.inactive = toggleActive !== "active"
      if (edit) {
        await updateProcess({
          variables: {
            id: edit.id,
            ...variables,
          },
        })
      } else {
        await createProcess({
          variables,
        })
        removeDraft()
        if (userflowEnabled) userflow.track("created_process")
      }
      handleClose()
    }
  }

  const handleLocationsPickedChanged = (picked) => {
    setLocations([...mapToIds(picked)])
  }

  const handleUsersGroupsPickedChanged = (picked) => {
    const newHasEveryone = picked.find((p) => p.__typename === "Everyone")
    setHasEveryone(newHasEveryone)
    setUsers(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "User"))])
    setGroups(newHasEveryone ? [] : [...mapToIds(picked.filter((p) => p.__typename === "Group"))])
  }

  const formValid = () =>
    isValid(repeat, name) &&
    steps.length > 0 &&
    (repeat.type === PROCESS_REPEAT.ADHOC || isValid(after)) &&
    (hasEveryone || users.length || groups.length) &&
    !isUploading

  const handleUpdateStep = (index, propName, value) => {
    let result
    if (propName === "expanded") {
      result = [...getCollapsedSteps(value && index)]
      setSteps(result)
    } else if (typeof value === "function") {
      result = value(steps)
      setSteps(result)
    } else if (steps[index][propName] !== value) {
      result = [...steps]
      result[index][propName] = value
      setSteps(result)
    }
    if (hasIterator(result)) {
      setDraftProperty("steps", [...result])
    }
    if (propName === "name" && index === steps.length - 1) setStepNameValid(value)
  }

  const handleAddStep = () => {
    setStepNameValid(false)
    const collapsed = getCollapsedSteps()
    collapsed.push({ ...initialStepState })
    setSteps([...collapsed])
    setDraftProperty("steps", [...collapsed])
  }

  const getCollapsedSteps = (expandedIndex) =>
    [...steps].map((s, index) => {
      s.expanded = typeof expandedIndex !== "undefined" && expandedIndex === index
      return s
    })

  const handleUploadingStateChange = (state) => {
    setIsUploading(state)
  }

  const handleRemoveStep = (index) => {
    setSteps((state) => {
      state.splice(index, 1)
      setDraftProperty("steps", [...state])
      return [...state]
    })
    setStepNameValid(true)
  }

  const handleUpdateBeforeSortStart = ({ index }) => {
    if (steps[index].expanded) {
      setSteps((state) => {
        state[index].expanded = false
        setDraftProperty("steps", [...state])
        return state
      })
    }
  }

  const handleSortEnd = ({ oldIndex, newIndex }) => {
    setSteps((state) => {
      const next = arrayMove(state, oldIndex, newIndex)
      setDraftProperty("steps", [...next])
      return [...next]
    })
  }

  const handleScheduleChanged = ({ after: newAfter, due: newDue, pinning: newPinning, repeat: newRepeat }) => {
    if (
      locations &&
      locations.length &&
      newRepeat.type === PROCESS_REPEAT.NEVER &&
      !scheduleDateToMoment(newAfter).isSame(scheduleDateToMoment(after), "day")
    )
      setOneOffScheduleChanged(true)

    setAfter(newAfter)
    setDue(newDue)
    setPinning(newPinning)
    setRepeat(newRepeat)

    if (!edit) {
      setDraft((prev) => ({
        ...prev,
        after: newAfter,
        due: newDue,
        pinning: newPinning,
        repeat: newRepeat,
      }))
    }
  }

  const handleInactiveChange = () => {
    const result = !inactive
    setInactive(result)
    setDraftProperty("inactive", result)
  }

  const isFormValid = formValid()

  const loading = createLoading || updateLoading

  const assignToDisabled = !locations || !locations.length

  const missingLocationAccess =
    edit && locations && locations.some((item) => !userLocations.some((ul) => toId(ul) === toId(item)))

  const activeJobCount = activeProcessJobsResult.data && activeProcessJobsResult.data.activeProcessJobs.length

  const disabled = missingLocationAccess

  const showDraft = !edit && !!draft && draftUntouched

  return (
    <>
      <MissingLocationAccessBlockquote show={missingLocationAccess} />

      <HasActiveJobsChangedBlockquote show={activeJobCount > 0 && !missingLocationAccess} count={activeJobCount} />

      <DraftBlockquote show={showDraft} subject="Process" onDiscard={handleDiscardDraft} />

      <Box mb={2}>
        <TextField
          label="Process name"
          variant="outlined"
          value={name}
          onChange={handleNameChange}
          fullWidth
          required
          onBlur={() => debounceTouched(setNameTouched)}
          error={isError(name, nameTouched)}
          helperText={getErrorMessage(name, nameTouched, "Please enter a name for the process")}
          disabled={disabled}
          inputProps={{ "data-cy": "TextField-title" }}
        />
      </Box>

      {steps.length > 0 && (
        <SortableListContainer
          items={steps}
          onUpdateStep={handleUpdateStep}
          onRemoveStep={handleRemoveStep}
          onUploadingStateChange={handleUploadingStateChange}
          useDragHandle
          updateBeforeSortStart={handleUpdateBeforeSortStart}
          onSortEnd={handleSortEnd}
          helperClass={classes.sortableListHelper}
          disabled={disabled}
        />
      )}

      <Box mb={3}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleAddStep}
          disabled={!nameValid || !stepNameValid || isUploading || disabled}
          data-cy="Button-add-step"
        >
          <AddIcon /> Add a step
        </Button>
      </Box>
      <Box mb={2}>
        {locations && userLocations.length > 1 && (
          <LocationsAssigner
            initialPicked={locations}
            onPickedChanged={handleLocationsPickedChanged}
            disabled={disabled}
            cy="ProcessCreator-locations"
          />
        )}
        {initialPicked && (
          <UsersGroupsAssigner
            title="Assign to"
            placeholder={assignToDisabled ? "No locations selected" : "Nobody selected"}
            allowSelf
            locations={locations.map((location) => toId(location))}
            topBorder={userLocations.length <= 1}
            openPicker={showAssigner}
            initialPicked={initialPicked}
            onPickedChanged={handleUsersGroupsPickedChanged}
            disabled={assignToDisabled || disabled}
            cy="ProcessCreator-to"
          />
        )}
        <ProcessScheduleAssigner
          process={{ id: edit ? edit.id : null, after, due, pinning, repeat }}
          topBorder={false}
          onChange={handleScheduleChanged}
          disabled={disabled}
          cy="ProcessCreator-schedule"
        />
        {(!edit || (edit && !edit.inactive)) && (
          <>
            <ColumnBox pt={1} pb={2} px={2}>
              <Checkbox
                type="label"
                color="primary"
                label="Make process inactive"
                checked={inactive}
                onChange={handleInactiveChange}
              />
              <Typography variant="caption">
                Stop the process running as scheduled. It will save as an inactive process where you can edit and
                publish it later.
              </Typography>
            </ColumnBox>
            <TextDivider />
          </>
        )}
      </Box>

      <HasActiveJobsScheduleChangedBlockquote
        show={activeJobCount > 0 && oneOffScheduleChanged}
        count={activeJobCount}
      />

      <CreatorActions
        id="ProcessCreator-CreatorActions"
        subject="Process"
        onClose={handleClose}
        onSubmit={handleSubmit}
        disableSubmit={!isFormValid || isUploading || loading || disabled}
        extraSubmits={
          edit !== null && edit.inactive
            ? [
                {
                  label: "Save process and make active",
                  onSubmit: (event) => handleSubmit(event, "active"),
                },
              ]
            : null
        }
        submitLoading={loading}
      />
    </>
  )
}

export default ProcessCreator
